import {
  poll2Lookup,
  poll2RunningTotal,
  formatSponsorLabel,
  type Poll2Draft,
  type Poll2Tier,
} from "./poll2Helpers";
import Button from "../../Components/Button";
import { fmtScore, MCI_MUTED } from "../../Utils/Helper/sponsorEngagementUi";
import { StatusBadge } from "../MyCheckIns/EngagementBadges";

type Account = {
  account_id: string;
  account_name: string;
  sponsor_name?: string;
  sponsor_role?: "primary" | "secondary" | string | null;
  team_feedback_received?: boolean;
  poll2_raw?: number | null;
  poll2_dimensions?: Record<string, number> | null;
  tier?: string | null;
};

type Props = {
  account: Account;
  isDraft: boolean;
  draft?: Poll2Draft;
  poll2Tiers: Poll2Tier[];
  showStatusBadge?: "draft-only" | "completed-only" | "none";
  onAction: () => void;
};

const TeamFeedbackAccountCard = ({
  account,
  isDraft,
  draft,
  poll2Tiers,
  showStatusBadge = "draft-only",
  onAction,
}: Props) => {
  const submitted = Boolean(account.team_feedback_received);
  const status = submitted ? "completed" : isDraft ? "draft" : "pending";
  const showBadge =
    (showStatusBadge === "draft-only" && isDraft && !submitted) ||
    (showStatusBadge === "completed-only" && submitted);
  const scoreDraft = draft ?? null;
  const total =
    submitted && account.poll2_dimensions
      ? poll2RunningTotal({
          involvement: account.poll2_dimensions.involvement ?? null,
          relationship: account.poll2_dimensions.relationship ?? null,
          knowledge: account.poll2_dimensions.knowledge ?? null,
          comments: "",
          evidence: {},
        })
      : scoreDraft
        ? poll2RunningTotal(scoreDraft)
        : null;
  const tierObj =
    submitted && total != null ? poll2Lookup(total, poll2Tiers) : null;
  const tierLabel = tierObj?.label ?? account.tier ?? null;
  const sponsorLabel = formatSponsorLabel(
    account.sponsor_name,
    account.sponsor_role
  );

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg px-4 py-3.5 shadow-sm flex flex-col gap-2 ${
        submitted
          ? ""
          : "cursor-pointer transition-all duration-150 hover:border-gray-300 hover:shadow hover:-translate-y-px"
      }`}
      role={submitted ? undefined : "button"}
      tabIndex={submitted ? undefined : 0}
      onClick={submitted ? undefined : onAction}
      onKeyDown={
        submitted
          ? undefined
          : e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onAction();
              }
            }
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-gray-800 leading-snug">
            {account.account_name}
          </div>
          {sponsorLabel ? (
            <div className={`text-[11px] ${MCI_MUTED} mt-0.5`}>
              {sponsorLabel}
            </div>
          ) : null}
        </div>
        {showBadge ? <StatusBadge status={status} /> : null}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {submitted && tierLabel ? (
          <span className={`text-[11px] ${MCI_MUTED}`}>{tierLabel}</span>
        ) : null}
      </div>
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100 mt-auto">
        {submitted && total != null ? (
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-gray-800">{total}</span>
            <span className={`text-[11px] ${MCI_MUTED}`}>/ 15</span>
          </div>
        ) : account.poll2_raw != null ? (
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-bold text-gray-800">
              {fmtScore(account.poll2_raw, 0)}
            </span>
            <span className={`text-[11px] ${MCI_MUTED}`}>raw</span>
          </div>
        ) : (
          <span className={`text-xs ${MCI_MUTED}`}>No score yet</span>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant={submitted ? "secondary" : "primary"}
            className="text-sm py-1 px-2.5"
            onClick={e => {
              e.stopPropagation();
              onAction();
            }}
          >
            {submitted ? "View" : isDraft ? "Resume" : "Start Feedback →"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamFeedbackAccountCard;
